import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../lib/api';
import { Badge } from '../../components/ui/badge';
import { Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';

export default function JobRunDetail() {
  const { runId } = useParams();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRun = async () => {
      try {
        const { data } = await api.get(`/jobs/runs/${runId}`);
        setRun(data);
      } catch (err) {
        console.error('Failed to fetch run:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchRun();
  }, [runId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!run) {
    return <div className="flex items-center justify-center h-screen text-muted-foreground">Run not found</div>;
  }

  const statusIcon = run.status === 'completed'
    ? <CheckCircle className="h-5 w-5 text-green-600" />
    : run.status === 'failed'
    ? <XCircle className="h-5 w-5 text-red-600" />
    : <Clock className="h-5 w-5 text-muted-foreground" />;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        {statusIcon}
        <div>
          <h1 className="text-xl font-bold" data-testid="run-detail-title">{run.job_name || run.job_id}</h1>
          <p className="text-sm text-muted-foreground">
            Run {run.id.slice(0, 8)} · {run.started_at ? new Date(run.started_at).toLocaleString() : ''} 
            {run.duration_sec > 0 && ` · ${run.duration_sec}s`}
            {run.triggered_by_name && ` · by ${run.triggered_by_name}`}
          </p>
        </div>
        <Badge variant={run.status === 'completed' ? 'default' : 'destructive'} className="ml-auto rounded-none">
          {run.status}
        </Badge>
      </div>

      {run.error && (
        <div className="bg-destructive/10 border border-destructive/20 p-4 rounded-sm">
          <p className="text-sm font-medium text-destructive">Error</p>
          <pre className="text-xs font-mono mt-1 whitespace-pre-wrap">{run.error}</pre>
        </div>
      )}

      {run.totals && Object.keys(run.totals).length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          {Object.entries(run.totals).map(([k, v]) => (
            <div key={k} className="border rounded-none p-3">
              <div className="text-2xl font-bold">{v}</div>
              <div className="text-xs text-muted-foreground">{k.replace(/_/g, ' ')}</div>
            </div>
          ))}
        </div>
      )}

      {run.customer_results && run.customer_results.length > 0 && (
        <div className="border rounded-none">
          <div className="bg-muted/50 p-3 font-medium text-sm">Customer Results ({run.customer_results.length})</div>
          <div className="divide-y">
            {run.customer_results.map((cr, i) => (
              <div key={i} className="p-4" data-testid={`customer-result-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm">{cr.customer_name || cr.account_id}</span>
                  {cr.errors && cr.errors.length > 0 ? (
                    <Badge variant="destructive" className="rounded-none text-xs">{cr.errors.length} error(s)</Badge>
                  ) : (
                    <Badge variant="outline" className="rounded-none text-xs border-green-500 text-green-700">OK</Badge>
                  )}
                </div>
                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                  {Object.entries(cr)
                    .filter(([k]) => !['customer_name', 'account_id', 'errors', 'mongo_account_id'].includes(k))
                    .map(([k, v]) => typeof v === 'number' ? (
                      <span key={k}>{k.replace(/_/g, ' ')}: <span className="font-mono font-medium text-foreground">{v}</span></span>
                    ) : null)}
                </div>
                {cr.errors && cr.errors.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {cr.errors.map((err, j) => (
                      <div key={j} className="text-xs font-mono text-destructive bg-destructive/5 p-1.5 rounded-sm">{err}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
